import React from "react";
import {
  Avatar,
  Badge,
  Box,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerOverlay,
  Flex,
  HStack,
  Icon,
  IconButton,
  Stack,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import {
  FiChevronsLeft,
  FiChevronsRight,
  FiHome,
  FiMessageCircle,
  FiPackage,
  FiPlus,
  FiShoppingBag,
  FiShoppingCart,
} from "react-icons/fi";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { colors, gradients, shadows } from "../UI/designSystem";

const NAV_ITEMS = [
  { label: "Home", icon: FiHome, to: "/home" },
  { label: "Medicines", icon: FiShoppingBag, to: "/medicine" },
  { label: "Cart", icon: FiShoppingCart, to: "/cart" },
  { label: "Orders", icon: FiPackage, to: "/orderDetails" },
];

const navMatches = (pathname, to) =>
  pathname === to || pathname.startsWith(`${to}/`) ||
  (to === "/orderDetails" && pathname.startsWith("/orderdetails"));

function Sidebar(props) {
  const {
    collapsed = false,
    onToggleSidebar,
    mobileOpen = false,
    onCloseMobile,
    brandName = "PillStack",
    username,
    fullName,
    chatLink,
    cartCount = 0,
  } = props;

  const location = useLocation();
  const navigate = useNavigate();
  const width = collapsed ? "74px" : "248px";
  const firstName = (fullName || username || "").toString().split(" ")[0] || "Patient";
  const isActive = (to) => navMatches(location.pathname, to);

  const closeMobile = () => onCloseMobile?.();

  const renderShell = (isMobile = false) => {
    const compact = collapsed && !isMobile;

    return (
      <>
        <Flex
          h="76px"
          align="center"
          justify={compact ? "center" : "space-between"}
          px={compact ? 2.5 : 4}
          borderBottom={`1px solid ${colors.neutral[100]}`}
        >
          {compact ? (
            <Box
              w="40px"
              h="40px"
              borderRadius="12px"
              bgGradient={gradients.brand}
              color="white"
              display="flex"
              alignItems="center"
              justifyContent="center"
              fontWeight="900"
              fontSize="14px"
              letterSpacing="-0.02em"
            >
              PS
            </Box>
          ) : (
            <HStack spacing={2.5} minW={0}>
              <Box
                w="36px"
                h="36px"
                borderRadius="10px"
                bgGradient={gradients.brand}
                color="white"
                display="flex"
                alignItems="center"
                justifyContent="center"
                fontWeight="900"
                fontSize="13px"
                letterSpacing="-0.02em"
                flexShrink={0}
              >
                PS
              </Box>
              <Box minW={0}>
                <Text fontSize="10px" fontWeight="800" color={colors.neutral[400]} textTransform="uppercase" letterSpacing="0.12em" m={0}>
                  {brandName}
                </Text>
                <Text fontSize="13px" fontWeight="800" color={colors.neutral[900]} m={0} noOfLines={1}>
                  Your Health Hub
                </Text>
              </Box>
            </HStack>
          )}
          {!isMobile ? (
            <IconButton
              aria-label={compact ? "Expand menu" : "Collapse menu"}
              icon={compact ? <FiChevronsRight /> : <FiChevronsLeft />}
              size="sm"
              variant="ghost"
              color={colors.neutral[500]}
              borderRadius="10px"
              _hover={{ bg: colors.neutral[50], color: colors.neutral[900] }}
              onClick={onToggleSidebar}
              display={compact ? "none" : "inline-flex"}
            />
          ) : null}
        </Flex>

        <Box px={compact ? 2 : 3} pt={4}>
          {compact ? (
            <Tooltip label="Upload Rx / New order" placement="right" hasArrow>
              <IconButton
                aria-label="Upload Rx / New order"
                icon={<FiPlus />}
                bgGradient={gradients.brand}
                color="white"
                borderRadius="12px"
                h="46px"
                w="100%"
                _hover={{ bgGradient: gradients.brandBold, boxShadow: shadows.brandHover }}
                onClick={() => navigate("/home")}
              />
            </Tooltip>
          ) : (
            <Flex
              as="button"
              type="button"
              onClick={() => {
                navigate("/home");
                closeMobile();
              }}
              align="center"
              gap={2.5}
              w="100%"
              h="46px"
              px={4}
              bgGradient={gradients.brand}
              color="white"
              borderRadius="12px"
              fontWeight="800"
              fontSize="14px"
              boxShadow={shadows.brand}
              _hover={{ bgGradient: gradients.brandBold, boxShadow: shadows.brandHover, transform: "translateY(-1px)" }}
              transition="all 0.18s ease"
            >
              <Icon as={FiPlus} boxSize={5} />
              Upload Rx / New Order
            </Flex>
          )}
        </Box>

        <Stack spacing={1.5} px={compact ? 2 : 3} py={4}>
          {NAV_ITEMS.map((item) => {
            const active = isActive(item.to);
            const content = (
              <Flex
                as={Link}
                to={item.to}
                onClick={closeMobile}
                align="center"
                justify={compact ? "center" : "flex-start"}
                gap={3}
                px={compact ? 0 : 3}
                h="46px"
                borderRadius="12px"
                bg={active ? colors.brand[50] : "transparent"}
                color={active ? colors.brand[700] : colors.neutral[600]}
                border={active ? `1px solid ${colors.brand[100]}` : "1px solid transparent"}
                _hover={{
                  bg: active ? colors.brand[50] : colors.neutral[50],
                  color: colors.neutral[900],
                  textDecoration: "none",
                }}
                transition="all 0.18s ease"
                position="relative"
              >
                {active && !compact ? (
                  <Box
                    position="absolute"
                    left="-3px"
                    top="25%"
                    bottom="25%"
                    w="3px"
                    borderRadius="full"
                    bgGradient={gradients.brand}
                  />
                ) : null}
                <Icon as={item.icon} boxSize={5} />
                {item.to === "/cart" && cartCount > 0 ? (
                  <Badge
                    position={compact ? "absolute" : "static"}
                    top={compact ? "8px" : undefined}
                    right={compact ? "10px" : undefined}
                    minW="18px"
                    h="18px"
                    px={1}
                    borderRadius="full"
                    bg={colors.accent[500]}
                    color="white"
                    fontSize="10px"
                    fontWeight="900"
                    display="inline-flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    {cartCount > 9 ? "9+" : cartCount}
                  </Badge>
                ) : null}
                {!compact ? (
                  <Text fontSize="14px" fontWeight="700" m={0}>
                    {item.label}
                  </Text>
                ) : null}
              </Flex>
            );

            return compact ? (
              <Tooltip key={item.label} label={item.label} placement="right" hasArrow>
                <Box>{content}</Box>
              </Tooltip>
            ) : (
              <Box key={item.label}>{content}</Box>
            );
          })}

          {chatLink ? (
            compact ? (
              <Tooltip label="Ask a pharmacist" placement="right" hasArrow>
                <Flex
                  as={Link}
                  to={chatLink}
                  onClick={closeMobile}
                  align="center"
                  justify="center"
                  h="46px"
                  borderRadius="12px"
                  color={isActive("/chatwithdoctor") ? colors.brand[700] : colors.neutral[600]}
                  bg={isActive("/chatwithdoctor") ? colors.brand[50] : "transparent"}
                  _hover={{ bg: colors.neutral[50], color: colors.neutral[900] }}
                >
                  <Icon as={FiMessageCircle} boxSize={5} />
                </Flex>
              </Tooltip>
            ) : (
              <Flex
                as={Link}
                to={chatLink}
                onClick={closeMobile}
                align="center"
                gap={3}
                px={3}
                h="46px"
                borderRadius="12px"
                color={isActive("/chatwithdoctor") ? colors.brand[700] : colors.neutral[600]}
                bg={isActive("/chatwithdoctor") ? colors.brand[50] : "transparent"}
                border={isActive("/chatwithdoctor") ? `1px solid ${colors.brand[100]}` : "1px solid transparent"}
                _hover={{ bg: colors.neutral[50], color: colors.neutral[900], textDecoration: "none" }}
                transition="all 0.18s ease"
              >
                <Icon as={FiMessageCircle} boxSize={5} />
                <Text fontSize="14px" fontWeight="700" m={0}>
                  Ask Pharmacist
                </Text>
              </Flex>
            )
          ) : null}
        </Stack>

        <Box mt="auto" px={compact ? 2 : 3} pb={3} pt={3} borderTop={`1px solid ${colors.neutral[100]}`}>
          {compact ? (
            <Tooltip label={fullName || username || "My Profile"} placement="right" hasArrow>
              <Flex justify="center">
                <Avatar name={fullName || username || "P"} size="sm" bg={colors.brand[500]} color="white" />
              </Flex>
            </Tooltip>
          ) : (
            <Box bg={colors.neutral[50]} borderRadius="14px" p={3} border={`1px solid ${colors.neutral[200]}`}>
              <HStack spacing={3}>
                <Avatar name={fullName || username || "P"} size="sm" bg={colors.brand[500]} color="white" />
                <Box flex="1" minW={0}>
                  <Text fontSize="13px" fontWeight="800" color={colors.neutral[900]} m={0} noOfLines={1}>
                    {firstName}
                  </Text>
                  <Text
                    as="button"
                    type="button"
                    fontSize="11px"
                    color={colors.brand[600]}
                    fontWeight="700"
                    m={0}
                    onClick={() => {
                      navigate("/home");
                      closeMobile();
                    }}
                    _hover={{ textDecoration: "underline" }}
                  >
                    View profile
                  </Text>
                </Box>
              </HStack>
            </Box>
          )}
        </Box>
      </>
    );
  };

  return (
    <>
      <Box
        position="fixed"
        left={0}
        top={0}
        bottom={0}
        w={width}
        bg="rgba(255,255,255,0.95)"
        backdropFilter="blur(18px)"
        borderRight={`1px solid ${colors.neutral[200]}`}
        boxShadow={shadows.md}
        transition="width 0.28s ease"
        zIndex={1100}
        overflow="hidden"
        display={{ base: "none", md: "flex" }}
        flexDirection="column"
      >
        {renderShell(false)}
      </Box>

      <Drawer isOpen={mobileOpen} placement="left" onClose={closeMobile} size="xs">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerBody p={0} display="flex" flexDirection="column">
            {renderShell(true)}
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      <Box
        position="fixed"
        left="0"
        right="0"
        bottom="0"
        zIndex={1200}
        bg="rgba(255,255,255,0.96)"
        backdropFilter="blur(14px)"
        borderTop={`1px solid ${colors.neutral[200]}`}
        px={2}
        py={2}
        display={{ base: "block", md: "none" }}
      >
        <HStack justify="space-between" spacing={1}>
          {NAV_ITEMS.map((item) => {
            const active = isActive(item.to);
            return (
              <Box
                key={item.label}
                as={Link}
                to={item.to}
                flex="1"
                textAlign="center"
                py={2}
                borderRadius="12px"
                bg={active ? colors.brand[50] : "transparent"}
                color={active ? colors.brand[700] : colors.neutral[600]}
              >
                <Box position="relative" display="inline-flex">
                  <Icon as={item.icon} boxSize={5} />
                  {item.to === "/cart" && cartCount > 0 ? (
                    <Badge
                      position="absolute"
                      top="-6px"
                      right="-10px"
                      minW="16px"
                      h="16px"
                      px={1}
                      borderRadius="full"
                      bg={colors.accent[500]}
                      color="white"
                      fontSize="9px"
                      fontWeight="900"
                    >
                      {cartCount > 9 ? "9+" : cartCount}
                    </Badge>
                  ) : null}
                </Box>
                <Text fontSize="10px" fontWeight="800" mt={1} m={0} noOfLines={1}>
                  {item.label}
                </Text>
              </Box>
            );
          })}
          {chatLink ? (
            <Box
              as={Link}
              to={chatLink}
              flex="1"
              textAlign="center"
              py={2}
              borderRadius="12px"
              bg={isActive("/chatwithdoctor") ? colors.brand[50] : "transparent"}
              color={isActive("/chatwithdoctor") ? colors.brand[700] : colors.neutral[600]}
            >
              <Icon as={FiMessageCircle} boxSize={5} />
              <Text fontSize="10px" fontWeight="800" mt={1} m={0} noOfLines={1}>
                Chat
              </Text>
            </Box>
          ) : null}
        </HStack>
      </Box>
    </>
  );
}

export default Sidebar;
